/**
 * TypeScript type definitions for the Webflow Field Formatting Module
 * 
 * This module provides comprehensive field formatting and validation
 * capabilities for Webflow forms, completely independent of the existing
 * lead tracking system.
 */

/**
 * Supported field types for formatting
 */
export type FormattingFieldType = 
  | 'phone'
  | 'currency'
  | 'percentage' 
  | 'number'
  | 'decimal'
  | 'text'
  | 'email'
  | 'date'
  | 'zip'
  | 'date_of_birth'
  | 'ssn'
  | 'name';

/**
 * Formatting configuration for individual fields
 */
export interface FieldFormattingConfig {
  /** The type of formatting to apply */
  type: FormattingFieldType;
  /** Whether to show real-time formatting as user types */
  realTime?: boolean;
  /** Whether to validate the field value */
  validate?: boolean;
  /** Custom validation rules */
  validationRules?: ValidationRule[];
  /** Formatting options specific to the field type */
  options?: FormattingOptions;
}

/**
 * Field-type specific formatting options
 */
export interface FormattingOptions {
  // Phone number options
  /** For phone numbers: default country code */
  defaultCountryCode?: string;
  
  // Currency options
  /** For currency: currency symbol to display */
  currencySymbol?: string;
  /** Whether to include currency symbol ($) in formatting */
  includeCurrencySymbol?: boolean;
  /** Allow decimal places in currency */
  allowDecimals?: boolean;
  /** Maximum decimal places allowed */
  maxDecimalPlaces?: number;
  /** Minimum allowed value */
  minValue?: number;
  /** Maximum allowed value */
  maxValue?: number;
  
  // General numeric options
  /** For numbers/decimals: number of decimal places */
  decimalPlaces?: number;
  /** For percentages: whether to include % symbol */
  includePercentSymbol?: boolean;
  
  // Date options
  /** For dates: date format pattern */
  dateFormat?: string;
  
  // Masking options
  /** Custom mask pattern (e.g., "999-999-9999" for phone) */
  maskPattern?: string;
  /** Whether to mask field after blur (for SSN, etc.) */
  maskAfterBlur?: boolean;
  /** Character to use for masking (default: '*') */
  maskCharacter?: string;
  /** For SSN: Whether to show full number during editing */
  showFullDuringEdit?: boolean;
  /** For SSN: Masking format to use */
  maskedFormat?: string;
}

/**
 * Validation rule definition
 */
export interface ValidationRule {
  /** Rule type identifier */
  type: string;
  /** Rule-specific parameters */
  params?: Record<string, any>;
  /** Custom error message for this rule */
  message?: string;
}

/**
 * Result of a formatting operation
 */
export interface FormattingResult {
  /** The formatted value */
  formattedValue: string;
  /** The raw/unformatted value */
  rawValue: string;
  /** Whether the formatting was successful */
  success: boolean;
  /** Any errors that occurred during formatting */
  errors?: string[];
}

/**
 * Result of a validation operation
 */
export interface ValidationResult {
  /** Whether the value is valid */
  isValid: boolean;
  /** List of validation errors */
  errors: string[];
  /** Validation warnings (non-blocking) */
  warnings?: string[];
}

/**
 * DOM element configuration for field formatting
 */
export interface FormattedFieldElement {
  /** The HTML input element */
  element: HTMLInputElement | HTMLTextAreaElement;
  /** Formatting configuration */
  config: FieldFormattingConfig;
  /** Current formatted value */
  formattedValue?: string;
  /** Current raw value */
  rawValue?: string;
  /** Whether the field is currently valid */
  isValid?: boolean;
  /** Last validation result */
  lastValidation?: ValidationResult;
  /** Cleanup function to remove event listeners */
  cleanup?: () => void;
}

/**
 * Configuration for the entire formatting module
 */
export interface FormatterModuleConfig {
  /** Whether to enable debug logging */
  debug?: boolean;
  /** Custom CSS classes for validation states */
  cssClasses?: {
    valid?: string;
    invalid?: string;
    warning?: string;
  };
  /** Whether to automatically scan and attach to form fields */
  autoScan?: boolean;
  /** Selectors to look for when auto-scanning */
  autoScanSelectors?: string[];
  /** Whether to gracefully degrade on errors */
  gracefulDegradation?: boolean;
  /** Whether to enable real-time formatting as user types */
  enableRealTimeFormatting?: boolean;
}

/**
 * Event data structure for formatting events
 */
export interface FormattingEvent {
  /** Type of event */
  type: 'format' | 'validate' | 'error' | 'init';
  /** Target field element */
  target: FormattedFieldElement;
  /** Event-specific data */
  data?: any;
  /** Timestamp of the event */
  timestamp: Date;
}

/**
 * Callback function type for formatting event listeners
 */
export type FormattingEventCallback = (event: FormattingEvent) => void;

/**
 * Interface for custom formatters
 */
export interface CustomFormatter {
  /** Formatter identifier */
  name: string;
  /** Function to format the value */
  format: (value: string, options?: FormattingOptions) => FormattingResult;
  /** Function to validate the value */
  validate?: (value: string, rules?: ValidationRule[]) => ValidationResult;
  /** Function to extract raw value from formatted value */
  extractRaw?: (formattedValue: string) => string;
}

/**
 * Error types that can occur in the formatting module
 */
export enum FormattingErrorType {
  INVALID_FIELD_TYPE = 'INVALID_FIELD_TYPE',
  ELEMENT_NOT_FOUND = 'ELEMENT_NOT_FOUND',
  CONFIGURATION_ERROR = 'CONFIGURATION_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  FORMATTING_ERROR = 'FORMATTING_ERROR',
  INITIALIZATION_ERROR = 'INITIALIZATION_ERROR'
}

/**
 * Custom error class for formatting operations
 */
export class FormattingError extends Error {
  constructor(
    public type: FormattingErrorType,
    message: string,
    public details?: any
  ) {
    super(message);
    this.name = 'FormattingError';
  }
}

/**
 * Field detection related types
 * Re-exported from detection system for convenience
 */

/**
 * Field detection confidence levels
 */
export enum FieldDetectionConfidence {
  HIGH = 1.0,        // Exact data-name match
  MEDIUM_HIGH = 0.8, // Regex data-name match  
  MEDIUM = 0.6,      // Field name fallback
  MEDIUM_LOW = 0.5,  // Input type inference
  LOW = 0.3,         // Placeholder inference
  NONE = 0.0         // No detection possible
}

/**
 * Field types supported by the detection engine
 */
export enum DetectableFieldType {
  PHONE = 'phone',
  ZIP = 'zip',
  DATE_OF_BIRTH = 'date_of_birth',
  CURRENCY = 'currency',
  SSN = 'ssn',
  UNKNOWN = 'unknown'
}

/**
 * Result of field detection analysis
 * Note: The main FieldDetectionResult is exported from detection-patterns.ts
 * This is kept for backward compatibility if needed
 */
export interface FormattingFieldDetectionResult {
  /** Detected field type */
  fieldType: DetectableFieldType;
  /** Detection confidence level */
  confidence: number;
  /** Detection method used */
  method: string;
  /** Whether the field requires formatting */
  requiresFormatting: boolean;
  /** Expected format pattern if applicable */
  expectedFormat?: string;
}

/**
 * Information about a detected field
 * Note: This interface is for backward compatibility.
 * The main FieldInfo is defined in field-registry.ts
 */
export interface DetectedFieldInfo {
  /** The HTML form element */
  element: HTMLInputElement | HTMLTextAreaElement;
  /** The data-name attribute value */
  dataName: string | null;
  /** Detected field type */
  fieldType: DetectableFieldType;
  /** Detection analysis result */
  detection: FormattingFieldDetectionResult;
  /** Current field value */
  currentValue: string;
  /** Whether the field is currently active/focused */
  isActive: boolean;
  /** Whether formatting has been applied */
  formatApplied?: boolean;
  /** Timestamp when field was detected */
  detectedAt: Date;
  /** Whether the field is currently valid */
  isValid?: boolean;
  /** Last validation error message */
  lastError?: string;
}